import { FuseResult } from 'fuse.js';
import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'gatsby-image';
import { CONTAINER_WIDTH_LARGE, OFFSET_SIDE } from '../../constants';
import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed';
import { Flex, Grid, Paragraph } from '../UIKit/system';
import { SearchCardProps } from './search-card';
import Link from '../link';
import mq from '../../utils/mq';
import IconButton from '../icon-button';
import { getThemeColor } from '../../utils/styles';
import Container from '../container';

interface ResultRailProps {
  heading: string;
  results: FuseResult<SearchCardProps>[] | null;
}

const totalMaxWidth = CONTAINER_WIDTH_LARGE + OFFSET_SIDE * 2;

export const ResultRail: FC<ResultRailProps> = (props) => {
  const { heading, results } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false });

  if (!results || results.length === 0) return null;

  const scrollPrev = () => {
    if (emblaApi?.canScrollPrev()) {
      emblaApi.scrollPrev();
    }
  };

  const scrollNext = () => {
    if (emblaApi?.canScrollNext()) {
      emblaApi.scrollNext();
    }
  };

  return (
    <Grid>
      <Container large>
        <Header>
          <Heading>{heading}</Heading>
          <Nav>
            <IconButton
              icon={{ name: 'chevronLeft2', strokeColor: 'black' }}
              onClick={scrollPrev}
              light
            />
            <IconButton
              icon={{ name: 'chevronRight2', strokeColor: 'black' }}
              onClick={scrollNext}
              light
            />
          </Nav>
        </Header>
      </Container>

      <Embla>
        <EmblaViewport ref={emblaRef}>
          <EmblaContainer>
            {results.map(({ item }) => (
              <Card key={item.id} to={`/${item.slug}`}>
                <ImageWrap>
                  <Image
                    fluid={getFluidGatsbyImage({ filename: item.image })}
                    style={{ height: 165 }}
                  />
                </ImageWrap>
                <Title as="p">{item.title}</Title>
              </Card>
            ))}
          </EmblaContainer>
        </EmblaViewport>
      </Embla>
    </Grid>
  );
};

const Header = styled(Grid).attrs(() => ({
  gridTemplateColumns: ['auto', 'auto', 'repeat(2, 1fr)'],
  pb: 24
}))``;

const Heading = styled(Paragraph).attrs(() => ({
  color: ['white', 'white', 'black'],
  fontSize: [16, 16, 18],
  fontWeight: 'bold'
}))`
  [aria-busy='true'] & {
    color: transparent;
  }
`;

const Nav = styled(Grid).attrs(() => ({
  display: ['none', 'none', 'grid'],
  gridAutoFlow: 'column',
  gridGap: 16,
  justifyContent: 'end'
}))``;

const Card = styled(Link).attrs(() => ({
  backgroundColor: ['white', 'white', 'lightGrey'],
  borderRadius: 8,
  minWidth: 0,
  target: '_blank'
}))`
  flex: 0 0 20%;

  ${mq.tablet(css`
    flex: 0 0 43%;
  `)}

  ${mq.mobile(css`
    flex: 0 0 80%;
  `)}
`;

const ImageWrap = styled(Grid).attrs(() => ({
  padding: '26px 26px 10px 26px'
}))``;

const Title = styled(Paragraph).attrs(() => ({
  fontSize: [14, 14, 16],
  fontWeight: 'bold',
  padding: '10px 26px 26px 26px',
  textAlign: 'center'
}))`
  a:hover &,
  a:active &,
  a:focus & {
    color: ${getThemeColor('primary')};
  }
`;

const Embla = styled(Grid).attrs(() => ({
  paddingLeft: [20, 40, `max(40px, calc(100% - ${totalMaxWidth}px) / 2 + 40px)`]
}))``;

const EmblaViewport = styled.div.attrs(() => ({
  className: 'embla__viewport'
}))`
  overflow: hidden;
`;

const EmblaContainer = styled(Flex)`
  gap: 20px;
`;
